import React, { useState } from 'react';
import Link from 'next/link';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';

import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import {
  Layout,
  Body,
  Card,
  Heading,
  FormDescription,
  Label,
  SubmitButton,
  FormInput,
  Text,
  PageHead,
} from '@/components/Index';
import { getOktaUser, signup } from '@/services/index';

const ShowError = dynamic(() => import('@/components/ShowError'));

export default function Signup() {
  const [responseError, setResponseError] = useState(false);
  const router = useRouter();

  const signupSchema = yup.object().shape({
    email: yup
      .string()
      .trim()
      .email('Please input the valid email address.')
      .required(),
  });

  const {
    setValue,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm({ resolver: yupResolver(signupSchema) });

  const onFormSubmit = async (payload) => {
    try {
      const user = await getOktaUser({ email: payload.email });
      if (user) {
        // user exists in okta. create vungle user and account and then show the login page
        try {
          await signup({ ...user, existsInOkta: true, is_pub: true });
        } catch (ex) {
          // if email already exists, don't throw error
          if (ex.code !== 403154) {
            throw ex;
          }
        }

        // okta redirect will remove the email from url. So, in the login page, we need to read it from the router
        // and set its value in session storage and based on that prefill the username field
        router.push({ pathname: '/login', query: { email: payload.email } });
      } else {
        // show signup page
        router.push({
          pathname: '/signup-new',
          query: { email: payload.email },
        });
      }
    } catch (e) {
      setResponseError(e);
    }
  };

  return (
    <>
      <PageHead
        title="Sign Up Preparation"
        description="Liftoff Sign Up Preparation page."
      />
      <Layout>
        <Body>
          <Card>
            <Heading text="Sign Up" />
            <FormDescription content="Create your account on Liftoff Monetize." />
            <form onSubmit={handleSubmit(onFormSubmit)}>
              <div className="mt-32">
                <Label htmlFor="email" text="Email" />
                <Controller
                  name="email"
                  control={control}
                  render={({ field }) => (
                    <FormInput
                      id="email"
                      name="email"
                      type="email"
                      isRequired={true}
                      placeholder="Enter email"
                      value={field.value}
                      dataTestId="user-email"
                      isErrored={!!errors.email}
                      errorMessages={errors.email?.message}
                      onChange={(value) => {
                        setValue('email', value, {
                          shouldValidate: true,
                        });
                      }}
                    />
                  )}
                />
              </div>
              {responseError && (
                <div className="mt-16">
                  <ShowError err={responseError} />
                </div>
              )}
              <div className="mt-32">
                <SubmitButton value="Next" />
              </div>
            </form>
            <div className="mt-32">
              <Text>
                Already have an account? <Link href="/login">Log in</Link>
              </Text>
            </div>
          </Card>
        </Body>
      </Layout>
    </>
  );
}
